/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, ChartLine, Megaphone, Users, ChatCircleDots, CurrencyInr, Globe } from "@phosphor-icons/react";
import { toast } from "sonner";

// EMBER-AD theme — distinct from Demographics (cobalt) & Revenue (gold)
const TH = {
  bg:        "#0A0807",
  surface:   "#15110F",
  surfaceHi: "#221915",
  border:    "#2E211B",
  borderHi:  "#54372A",
  accent:    "#FF7A3D",      // ember orange
  accentDim: "#A04A20",
  warm:      "#FFC178",
  pop:       "#F8E27A",
  green:     "#5CD79B",
  text:      "#FAEFE6",
  textMute:  "#9C8B7F",
  textDim:   "#6A5950",
};

const PERIODS = [
  { key: "today", label: "Last 24h" },
  { key: "7d",    label: "Last 7 days" },
  { key: "30d",   label: "Last 30 days" },
  { key: "90d",   label: "Last 90 days" },
  { key: "ytd",   label: "Year-to-Date" },
  { key: "all",   label: "All-time" },
];

export default function AdminAdsPerformancePage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [period, setPeriod] = useState("7d");
  const [onlyAds, setOnlyAds] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_super_admin) {
      toast.error("Ads performance is a Super-Admin tool.");
      navigate("/", { replace: true });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user?.is_super_admin) return;
    setLoading(true);
    api.get("/super-admin/ads/funnel", { params: { period, only_ads: onlyAds } })
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load ads funnel."))
      .finally(() => setLoading(false));
  }, [user, period, onlyAds]);

  const t = data?.totals;

  return (
    <div className="min-h-screen" style={{ backgroundColor: TH.bg, color: TH.text }}>
      <Header />
      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-14">
        <button type="button" onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] mb-7 transition-opacity hover:opacity-100"
          style={{ color: TH.textMute, opacity: 0.7 }}
          data-testid="ads-back">
          <ArrowLeft size={12} weight="bold" /> Back
        </button>

        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.25em] mb-5"
          style={{ backgroundColor: `${TH.accent}1A`, color: TH.accent, border: `1px solid ${TH.accentDim}` }}>
          <Megaphone size={11} weight="bold" /> Super-Admin · Ad Performance
        </div>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light leading-tight">
          Google Ads <span className="italic" style={{ color: TH.accent }}>Funnel</span>
        </h1>
        <p className="mt-4 max-w-3xl text-base leading-relaxed font-light" style={{ color: TH.textMute }}>
          First-party attribution from <b style={{ color: TH.text }}>ad click</b> →
          <b style={{ color: TH.text }}> signup</b> →
          <b style={{ color: TH.text }}> first chat</b> →
          <b style={{ color: TH.text }}> first paid top-up</b>. Captured via
          <code className="mx-1 px-1 py-0.5 rounded text-xs" style={{ background: TH.surfaceHi, color: TH.warm }}>gclid</code>
          on every landing — independent of Google Ads' own conversion reporting, so you see what's truly
          happening on YOUR side.
        </p>

        {/* Important production note */}
        <div className="mt-5 rounded-xl p-4 text-sm flex gap-3" data-testid="ads-prod-note"
          style={{ background: `${TH.warm}10`, border: `1px solid ${TH.warm}33`, color: TH.warm }}>
          <span className="text-base">⚠</span>
          <div className="leading-relaxed">
            <b>Important:</b> This funnel only fills in once visitors arrive at the LIVE site
            (<code className="text-xs">astrokuberchat.com</code>) <b>after</b> deployment. Older Google Ads
            clicks before deploy don't get back-filled. After deploy, hit "refresh" here.
          </div>
        </div>

        {/* Filters */}
        <div className="mt-7 flex flex-wrap items-center gap-2" data-testid="ads-period-tabs">
          {PERIODS.map((p) => (
            <button key={p.key} type="button" onClick={() => setPeriod(p.key)}
              className="px-4 py-2 rounded-full font-mono text-xs uppercase tracking-wider transition-all"
              style={{
                backgroundColor: period === p.key ? TH.accent : "transparent",
                color: period === p.key ? TH.bg : TH.textMute,
                border: `1px solid ${period === p.key ? TH.accent : TH.border}`,
                fontWeight: period === p.key ? 700 : 400,
              }}
              data-testid={`ads-period-${p.key}`}>
              {p.label}
            </button>
          ))}
          <button type="button" onClick={() => setOnlyAds((v) => !v)}
            className="ml-2 px-3 py-2 rounded-full font-mono text-[10px] uppercase tracking-wider transition-all"
            style={{
              backgroundColor: onlyAds ? `${TH.green}22` : "transparent",
              color: onlyAds ? TH.green : TH.textMute,
              border: `1px solid ${onlyAds ? TH.green : TH.border}`,
            }}
            data-testid="ads-toggle-onlyads">
            {onlyAds ? "Filter: ad traffic only" : "Filter: ALL landings"}
          </button>
        </div>

        {loading && (
          <div className="mt-10 text-center font-mono text-sm" style={{ color: TH.textDim }}>
            Computing ad-funnel…
          </div>
        )}

        {!loading && t && (
          <>
            {/* KPI cards */}
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="ads-kpis">
              <KPI label="Ad clicks (landings)" value={t.clicks} hint="Distinct sessions with gclid"
                icon={<Megaphone size={16} weight="duotone" />} />
              <KPI label="Signed up" value={t.signed_up} hint={`${t.click_to_signup_pct}% of clicks`}
                icon={<Users size={16} weight="duotone" />} accent={TH.warm} />
              <KPI label="Opened first chat" value={t.first_chat} hint={`${t.signup_to_chat_pct}% of signups`}
                icon={<ChatCircleDots size={16} weight="duotone" />} accent={TH.pop} />
              <KPI label="First paid top-up" value={t.first_paid}
                hint={`${t.chat_to_paid_pct}% of chats · ${t.overall_paid_pct}% end-to-end`}
                icon={<CurrencyInr size={16} weight="duotone" />} accent={TH.green} />
            </div>

            {/* Revenue strip */}
            <div className="mt-4 rounded-xl px-5 py-4 flex items-center justify-between"
              style={{ background: TH.surface, border: `1px solid ${TH.border}` }} data-testid="ads-revenue">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: TH.textMute }}>
                  Revenue from ad-attributed first top-ups
                </div>
                <div className="text-3xl font-light mt-1" style={{ color: TH.green }}>
                  ₹{Number(t.revenue_inr).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                </div>
              </div>
              <div className="text-right font-mono text-[10px]" style={{ color: TH.textDim }}>
                ROAS = revenue ÷ Google-Ads-spend.<br/>Wire-up the Ads API to auto-compute.
              </div>
            </div>

            <Funnel t={t} />

            {/* Per-day series */}
            <DaySeries rows={data.series} />

            {/* Country breakdown */}
            <CountryTable rows={data.countries} />

            {/* Campaign breakdown */}
            <CampaignTable rows={data.campaigns} />

            {/* Recent landings */}
            <RecentLandings rows={data.recent} />
          </>
        )}

        {!loading && t && t.clicks === 0 && (
          <div className="mt-8 rounded-xl p-6 text-sm" style={{ background: TH.surface, border: `1px solid ${TH.border}`, color: TH.textMute }}>
            <b style={{ color: TH.text }}>No ad-tracked clicks recorded yet.</b>
            <ul className="mt-3 list-disc list-inside space-y-1">
              <li>Verify <i>Auto-tagging</i> is ON in your Google Ads account (it appends <code className="text-xs px-1" style={{ background: TH.surfaceHi, color: TH.warm }}>gclid</code> to every URL).</li>
              <li>Open one of your live ad URLs and confirm the URL has <code className="text-xs px-1" style={{ background: TH.surfaceHi, color: TH.warm }}>?gclid=…</code>.</li>
              <li>Make sure the visit was on the deployed site (<code className="text-xs">astrokuberchat.com</code>) — preview clicks aren't tracked.</li>
              <li>Tracking starts <b>only after</b> the latest deploy.</li>
            </ul>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

// =============================================================================
const KPI = ({ label, value, hint, icon, accent }) => (
  <div className="rounded-xl p-5" style={{ backgroundColor: TH.surface, border: `1px solid ${TH.border}` }}
    data-testid="ads-kpi">
    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: TH.textMute }}>
      {icon} {label}
    </div>
    <div className="mt-2 text-3xl font-light" style={{ color: accent || TH.accent }}>{value}</div>
    <div className="mt-1 text-xs font-light" style={{ color: TH.textDim }}>{hint}</div>
  </div>
);

const Funnel = ({ t }) => {
  const max = Math.max(1, t.clicks);
  const rows = [
    { k: "Clicks",          v: t.clicks,    rate: 100,                       color: TH.accent },
    { k: "Signed up",       v: t.signed_up, rate: t.click_to_signup_pct,     color: TH.warm },
    { k: "Opened first chat", v: t.first_chat, rate: (t.clicks ? Math.round(100 * t.first_chat / t.clicks) : 0), color: TH.pop },
    { k: "First paid",      v: t.first_paid, rate: t.overall_paid_pct,       color: TH.green },
  ];
  return (
    <div className="mt-6 rounded-xl p-5" style={{ background: TH.surface, border: `1px solid ${TH.border}` }} data-testid="ads-funnel">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] mb-4" style={{ color: TH.textMute }}>
        <ChartLine size={12} weight="bold" /> Conversion funnel
      </div>
      <ul className="space-y-2.5">
        {rows.map((r) => (
          <li key={r.k} className="grid grid-cols-12 items-center gap-3" data-testid={`ads-funnel-${r.k.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="col-span-3 text-sm" style={{ color: TH.text }}>{r.k}</div>
            <div className="col-span-7 h-2.5 rounded-full overflow-hidden" style={{ background: TH.surfaceHi }}>
              <div className="h-full rounded-full transition-all"
                style={{ width: `${Math.max(2, (r.v / max) * 100)}%`, background: r.color, boxShadow: `0 0 12px ${r.color}77` }} />
            </div>
            <div className="col-span-1 text-right font-mono text-sm font-bold">{r.v}</div>
            <div className="col-span-1 text-right font-mono text-xs" style={{ color: TH.textMute }}>{r.rate}%</div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const DaySeries = ({ rows }) => {
  if (!rows?.length) return null;
  const max = Math.max(1, ...rows.map((r) => r.clicks));
  return (
    <div className="mt-6 rounded-xl p-5" style={{ background: TH.surface, border: `1px solid ${TH.border}` }} data-testid="ads-day-series">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] mb-4" style={{ color: TH.textMute }}>
        Daily ad-funnel breakdown
      </div>
      <div className="flex items-end gap-1 sm:gap-1.5 h-36">
        {rows.map((r) => {
          const h = max ? Math.max(2, (r.clicks / max) * 100) : 2;
          return (
            <div key={r.day} className="flex-1 group relative">
              <div className="w-full rounded-t-sm transition-all cursor-pointer"
                style={{ height: `${h}%`, background: `linear-gradient(180deg, ${TH.warm}, ${TH.accent})`, opacity: 0.8 }}
                title={`${r.day}: ${r.clicks} clicks · ${r.signed_up} signups · ${r.first_chat} chats · ${r.first_paid} paid`} />
              <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10 text-[10px] font-mono whitespace-nowrap"
                style={{ backgroundColor: TH.bg, border: `1px solid ${TH.borderHi}`, color: TH.text }}>
                <div className="font-bold">{r.day}</div>
                <div>{r.clicks} clicks · {r.signed_up} su · {r.first_chat} chat · {r.first_paid} pay</div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex justify-between font-mono text-[9px]" style={{ color: TH.textDim }}>
        <span>{rows[0]?.day}</span>
        <span>{rows[rows.length - 1]?.day}</span>
      </div>
    </div>
  );
};

const CountryTable = ({ rows }) => {
  if (!rows?.length) return null;
  const max = Math.max(...rows.map((r) => r.clicks));
  return (
    <div className="mt-6 rounded-xl p-5" style={{ background: TH.surface, border: `1px solid ${TH.border}` }} data-testid="ads-countries">
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: TH.textMute }}>
          <Globe size={12} weight="bold" /> Where the clicks come from
        </div>
        <div className="font-mono text-[10px]" style={{ color: TH.textDim }}>
          {rows.length} {rows.length === 1 ? "country" : "countries"}
        </div>
      </div>
      <ul className="space-y-2.5">
        {rows.slice(0, 12).map((r) => (
          <li key={r.country} className="grid grid-cols-12 items-center gap-2 sm:gap-3" data-testid={`ads-country-${r.country}`}>
            <div className="col-span-2 font-mono text-xs" style={{ color: TH.warm }}>{r.country}</div>
            <div className="col-span-6 h-2 rounded-full overflow-hidden" style={{ background: TH.surfaceHi }}>
              <div className="h-full rounded-full"
                style={{ width: `${Math.max(3, (r.clicks / max) * 100)}%`, background: `linear-gradient(90deg, ${TH.accent}, ${TH.pop})`, boxShadow: `0 0 12px ${TH.accent}55` }} />
            </div>
            <div className="col-span-1 text-right font-mono text-xs">{r.clicks}</div>
            <div className="col-span-1 text-right font-mono text-xs" style={{ color: TH.warm }}>{r.signed_up}</div>
            <div className="col-span-1 text-right font-mono text-xs" style={{ color: TH.pop }}>{r.first_chat}</div>
            <div className="col-span-1 text-right font-mono text-xs" style={{ color: TH.green }}>{r.first_paid}</div>
          </li>
        ))}
      </ul>
      <div className="mt-3 grid grid-cols-12 items-center gap-2 sm:gap-3 font-mono text-[9px] uppercase tracking-wider" style={{ color: TH.textDim }}>
        <div className="col-span-2">Country</div>
        <div className="col-span-6"></div>
        <div className="col-span-1 text-right">Clicks</div>
        <div className="col-span-1 text-right">Signup</div>
        <div className="col-span-1 text-right">Chat</div>
        <div className="col-span-1 text-right">Paid</div>
      </div>
    </div>
  );
};

const CampaignTable = ({ rows }) => {
  if (!rows?.length) return null;
  return (
    <div className="mt-6 rounded-xl p-5" style={{ background: TH.surface, border: `1px solid ${TH.border}` }} data-testid="ads-campaigns">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] mb-4" style={{ color: TH.textMute }}>
        Campaign breakdown (utm_campaign)
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="font-mono text-[9px] uppercase tracking-wider" style={{ color: TH.textDim }}>
            <th className="text-left py-1">Campaign</th>
            <th className="text-right">Clicks</th>
            <th className="text-right">Signups</th>
            <th className="text-right">Chats</th>
            <th className="text-right">Paid</th>
            <th className="text-right">Revenue</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 10).map((r) => (
            <tr key={r.campaign} className="border-t" style={{ borderColor: TH.border }} data-testid={`ads-campaign-${r.campaign}`}>
              <td className="py-2 truncate max-w-[260px]" style={{ color: TH.text }}>{r.campaign}</td>
              <td className="text-right font-mono">{r.clicks}</td>
              <td className="text-right font-mono" style={{ color: TH.warm }}>{r.signed_up}</td>
              <td className="text-right font-mono" style={{ color: TH.pop }}>{r.first_chat}</td>
              <td className="text-right font-mono" style={{ color: TH.green }}>{r.first_paid}</td>
              <td className="text-right font-mono" style={{ color: TH.green }}>₹{Number(r.revenue_inr).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const RecentLandings = ({ rows }) => {
  if (!rows?.length) return null;
  const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—";
  return (
    <div className="mt-6 rounded-xl p-5 overflow-x-auto" style={{ background: TH.surface, border: `1px solid ${TH.border}` }} data-testid="ads-recent">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] mb-4" style={{ color: TH.textMute }}>
        Recent ad landings (last 30)
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="font-mono text-[9px] uppercase tracking-wider" style={{ color: TH.textDim }}>
            <th className="text-left py-1">Landed</th>
            <th className="text-left">gclid</th>
            <th className="text-left">Country</th>
            <th className="text-left">Path</th>
            <th className="text-center">Signup</th>
            <th className="text-center">Chat</th>
            <th className="text-right">Paid</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.session_id} className="border-t" style={{ borderColor: TH.border }} data-testid="ads-recent-row">
              <td className="py-2 font-mono text-[10px]" style={{ color: TH.textMute }}>{fmt(r.landed_at)}</td>
              <td className="font-mono text-[10px]" style={{ color: TH.warm }}>{r.gclid || "—"}</td>
              <td className="font-mono text-[10px]">{r.country}</td>
              <td className="font-mono text-[10px] truncate max-w-[200px]" style={{ color: TH.textMute }}>{r.landing_path || "/"}</td>
              <td className="text-center">{r.signed_up_at ? "✓" : "—"}</td>
              <td className="text-center">{r.first_chat_at ? "✓" : "—"}</td>
              <td className="text-right font-mono" style={{ color: r.first_paid_at ? TH.green : TH.textDim }}>
                {r.first_paid_at ? `₹${r.first_paid_amount_inr || 0}` : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
